import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import { SWRConfiguration } from 'swr';
import { v4 as uuidv4 } from 'uuid';

import { AnalyticsReportSettingsProps } from '~/data/reports';
import { UseArraySWROutput, useArraySWR } from '~/hooks/useSWR';
import { fetchFromOldApi } from '~/services/Http';

export enum SavedReportSettingsType {
  DynamicPersonnelReport = 'PERSONNEL_CUSTOM',
  FTEReport = 'FULL_TIME_EQUIVALENT',
  OpenWorkflowEmploymentDetails = 'OPEN_WORKFLOW_EMPLDET',
  OpenWorkflowInternalPositions = 'OPEN_WORKFLOW_INTPOS',
  OpenWorkflowRewarding = 'OPEN_WORKFLOW_REWARDING',
  ClosedWorkflowEmploymentDetails = 'CLOSED_WORKFLOW_EMPLDET',
  ClosedWorkflowInternalPositions = 'CLOSED_WORKFLOW_INTPOS',
  ClosedWorkflowRewarding = 'CLOSED_WORKFLOW_REWARDING',
  WorktimeReport = 'TIME_REPORTING',
  WorkingHoursSummaryReport = 'WORKING_HOURS_SUMMARY_REPORT',
  WorktimeDimensionReport = 'WORKTIME_DIMENSION_REPORT',
}

export enum SavedReportSettingsPrivacyType {
  PERSONAL = 'PERSONAL',
  SHARED = 'COMMON',
}

export interface Entity {
  id: string;
}

export interface UpdatableSavedReportSettingsData {
  name: string;
  reportType: string;
  privacyType: SavedReportSettingsPrivacyType;
  activeColumns: string;
}

export type SavedReportDataGridSettingsConfig = {
  columnVisibility?: GridColumnVisibilityModel;
  columnSorting?: GridSortModel;
  columnFiltering?: GridFilterModel;
};

export type SavedReportSettingsConfig<P> = {
  reportSelection?: AnalyticsReportSettingsProps<P>;
} & SavedReportDataGridSettingsConfig;

export type SavedReportSettings<P> = Entity &
  UpdatableSavedReportSettingsData & {
    settingsConfig?: SavedReportSettingsConfig<P>;
    createdBy: number;
    createdAt: string;
    updatedBy: number;
    updatedAt: string;
  };

const getSavedReportSettings = <P>() => {
  return fetchFromOldApi('/json/report/settings').then(
    (r): Promise<SavedReportSettings<P>[]> => r.json(),
  );
};

const deleteSavedReportSettings = (reportSettingsId: string): Promise<void> =>
  fetchFromOldApi(`/json/report/settings/${reportSettingsId}`, {
    method: 'DELETE',
  }).then((r) => r.json());

const submitSavedReportSettings = <P>(props: {
  reportSettingsId?: string;
  data: UpdatableSavedReportSettingsData;
}): Promise<SavedReportSettings<P>> => {
  const { reportSettingsId, data } = props;

  const method = !!reportSettingsId ? 'PUT' : 'POST';
  const url = `/json/report/settings/${
    !!reportSettingsId ? reportSettingsId : 'create'
  }`;
  const payload = !!reportSettingsId ? { ...data } : { ...data, id: uuidv4() };

  return fetchFromOldApi(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then((r) => r.json());
};

export const useSavedReportSettingsData = <P>(
  swrConfig?: SWRConfiguration,
  disabled?: boolean,
): UseArraySWROutput<SavedReportSettings<P>> => {
  const swrConfiguration: SWRConfiguration = {
    revalidateOnFocus: false,
    ...swrConfig,
  };

  return useArraySWR(
    !disabled ? ['getSavedReportSettings'] : null,
    ([_key]) => getSavedReportSettings(),
    swrConfiguration,
  );
};

export type UseSavedReportSettingsOutput<P> = UseArraySWROutput<
  SavedReportSettings<P>
> & {
  onDeleteReportSettings: (reportSettingsId: string) => Promise<void>;
  onSaveReportSettings: (submitProps: {
    reportSettingsId?: string;
    data: UpdatableSavedReportSettingsData;
  }) => Promise<SavedReportSettings<P>>;
};

const parseSavedReportSettings = <P>(
  settings: SavedReportSettings<P>,
): SavedReportSettings<P> => {
  const settingsConfig = JSON.parse(settings.activeColumns);
  return { ...settings, settingsConfig };
};

export const useSavedReportSettings = <P>(
  props: {
    reportType: SavedReportSettingsType;
  },
  swrConfig?: SWRConfiguration,
  disabled?: boolean,
): UseSavedReportSettingsOutput<P> => {
  const { reportType } = props;

  const { data, ...rest } = useSavedReportSettingsData<P>(swrConfig, disabled);
  const { mutate } = rest;

  const reportTypeData = useMemo(() => {
    const isReportVersion2 = (settings: SavedReportSettings<P>) => {
      const config = JSON.parse(settings.activeColumns);
      return (
        config.hasOwnProperty('reportVersion') && config.reportVersion === 2
      );
    };

    return data
      .filter(
        (settings) =>
          settings.reportType === reportType && isReportVersion2(settings),
      )
      .map(parseSavedReportSettings);
  }, [data, reportType]);

  const onDeleteReportSettings = useCallback(
    async (reportSettingsId: string) => {
      return deleteSavedReportSettings(reportSettingsId).then(() => {
        mutate();
      });
    },
    [mutate],
  );

  const onSaveReportSettings = useCallback(
    async (submitProps: {
      reportSettingsId?: string;
      data: UpdatableSavedReportSettingsData;
    }): Promise<SavedReportSettings<P>> => {
      return submitSavedReportSettings<P>(submitProps).then((data) => {
        return mutate().then(() => {
          return parseSavedReportSettings(data);
        });
      });
    },
    [mutate],
  );

  return {
    data: reportTypeData,
    ...rest,
    onDeleteReportSettings,
    onSaveReportSettings,
  };
};
